import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Header from '@/components/ui/custom/table/Header';
import Filter from '@/components/filter/Filter';
import { LoadingIcon } from '@/components/icons';
import { MANAGEMENT_PAGE_SIZE as pageSize } from '@/constants';
import { useDynamicParams } from '@/data/hook/useDynamicParams';
import useListService from '@/data/hook/useListService';
import { HackathonFilters } from '@/data/services/hackathonServices';
import { hackathonsQueryKeys } from '@/data/services/querykeys';
import { FilterProps } from '@/models/FilterConfig';
import PaginationComponent from '@/pages/admin/PaginationComponent';
import { KlassHeader } from '@/utils/HeaderTypes';
import { Fields, searchParamsToObject } from '@/utils/searchParamsToObject';
import DataHackathonTable from './DataHackathonTable';

export default function ListHackathon() {
  const { t: tManage } = useTranslation('translation', {
    keyPrefix: 'manageHackathon',
  });

  const { t: tTitles } = useTranslation('translation', {
    keyPrefix: 'manageHackathon.titles',
  });
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { params, updateParams } = useDynamicParams<HackathonFilters>();

  const configFields: Fields<HackathonFilters> = {
    search: { type: 'string' },
    pageNumber: { type: 'number' },
    pageSize: { type: 'number' },
  };

  const filter = searchParamsToObject<HackathonFilters>(
    params,
    configFields,
    {},
  );

  function onChange(data: HackathonFilters) {
    updateParams({ ...data, pageNumber: 1 });
  }

  const {
    results: hackathons,
    meta,
    isInitialLoading,
    isPreviousData,
    invalidate,
  } = useListService({
    ...hackathonsQueryKeys.list({ ...filter, pageSize }),
  });

  const loading = isInitialLoading || isPreviousData;

  const header: KlassHeader = {
    klass: {
      title: tTitles('hackathon'),
      size: 30,
    },
    start: {
      title: tTitles('theme'),
      size: 30,
    },
    end: {
      title: tTitles('content'),
      size: 20,
    },
    actions: { title: t('actions'), size: 10 },
  };

  const changePages = (numberOfPage: string) => {
    updateParams({ pageNumber: Number(numberOfPage) });
  };

  const pagination = {
    params,
    amountOf: meta?.pagination.count || 0,
    amountPages: meta?.pagination.pages || 1,
    paramsPage: filter.pageNumber || 1,
    isLoading: isInitialLoading || isPreviousData,
    changePages,
  };

  const configs: FilterProps[] = [
    {
      type: 'search',
      label: t('hackathon'),
      debounce: true,
      name: 'search',
      placeholder: t('research'),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Filter
        configs={configs}
        onChange={onChange}
        filter={filter}
        className={{ base: 'xl:flex-row justify-between' }}
      />

      <table className="w-full flex flex-col gap-y-2.5">
        <Header headers={Object.values(header)} />
        <ConditionalRenderer condition={!loading}>
          <DataHackathonTable
            titles={header}
            hackathons={hackathons}
            onDelete={invalidate}
          />
        </ConditionalRenderer>
      </table>

      <ConditionalRenderer condition={!hackathons.length && !loading}>
        <Text
          text={tManage('noHackathons')}
          className="flex justify-center mt-5"
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={loading}>
        <div className="flex w-full h-[518px] justify-center items-center p-3.5">
          <LoadingIcon className="text-primary/40 w-10 h-10" />
        </div>
      </ConditionalRenderer>

      <PaginationComponent managerType="hackathons" {...pagination} />
    </div>
  );
}
